import React, { useState, useEffect, useRef } from 'react';
import GlobalFunction from './globalFunction';
import EventSystem from './EventSystem';
import { Tile, Body, LoggingIndicator } from './style/tile.style';
import './css/chatModule.css';

export default function ChatControlModule({ parameters, onSendMessage, enableModuleLogging, addLogEntry }) {
    const containerRef = useRef(null);

    const storageKey = parameters.config.module_name + '_' + parameters.config.event_screen_module_id;
    const savedName = JSON.parse(localStorage.getItem(storageKey)) ?? '';

    const [chatParams, setChatParams] = useState({ message: '', name: savedName });
    const [messageSequence, setMessageSequence] = useState(0);
    const [dataRow, setDataRow] = useState({ line1Height: 34, line2Height: 10, fontSize: 14 });
    const [loggingModule, setLoggingModule] = useState(false);
    const [highlight, setHighlight] = useState(false);

    function updateDimensions() {
        const containerH = parameters.containerHeight.container;
        const row = {};
        if (containerH < 76) {
            row.line1Height = Math.floor((containerH - 10) / 2);
            row.line2Height = Math.floor((containerH - 10) / 2);
        } else {
            row.line1Height = 34;
            row.line2Height = containerH - 7 - 34;
        }
        row.fontSize = Math.min(Math.floor(row.line1Height * 0.6), 14);
        setDataRow(row);
    }

    useEffect(() => {
        enableModuleLogging();
        window.addEventListener('resize', updateDimensions);
        updateDimensions();

        const moduleId = parameters.config.event_screen_module_id;
        EventSystem.subscribe('logStatus' + moduleId, setLoggingModule);
        EventSystem.subscribe('highlightModule' + moduleId, setHighlight);

        return () => {
            window.removeEventListener('resize', updateDimensions);
            EventSystem.unsubscribe('logStatus' + moduleId, setLoggingModule);
            EventSystem.unsubscribe('highlightModule' + moduleId, setHighlight);
        };
    }, []);

    function sendMessage() {
        if (chatParams.message.length === 0) return;
        onSendMessage({
            action: 'chat',
            sourceModuleName: parameters.config.module_name,
            event_grouping_seq: parameters.config.event_grouping_seq,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
            chatParams: { ...chatParams, senderSequence: messageSequence },
        });
        addLogEntry('Send ' + messageSequence + ': ' + chatParams.message);
        setChatParams(prev => ({ ...prev, message: '' }));
        setMessageSequence(prev => prev + 1);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    }

    function handleChange(e) {
        if (e.target.name === 'message' && e.target.value === '\n') return;
        const newParams = { ...chatParams, [e.target.name]: e.target.value };
        setChatParams(newParams);
        localStorage.setItem(storageKey, JSON.stringify(newParams.name));
    }

    const bodyHeight = parameters.containerHeight.container - 2 - 3;
    const palColour = GlobalFunction.getPaletteColour(parameters.config.colour) + '66';

    const inputNameFieldStyle = {
        width: '40%', marginLeft: '10px', height: dataRow.line1Height + 'px',
        lineHeight: dataRow.line1Height + 'px', paddingTop: '3%',
        backgroundColor: parameters.colours.backgroundColor, borderRadius: 0,
        color: parameters.colours.color, borderStyle: 'none',
        borderBottom: '1px solid ' + palColour,
    };
    const inputMessageFieldStyle = {
        width: 'calc(100% - 85px)', marginLeft: '10px', marginRight: '10px',
        height: dataRow.line2Height - 15 + 'px',
        backgroundColor: parameters.colours.backgroundColor,
        position: 'absolute', bottom: '15px',
        color: parameters.colours.color, borderStyle: 'none',
        resize: 'none', borderRadius: 0,
        borderBottom: '1px solid ' + palColour,
    };

    return (
        <Tile ref={containerRef} highlight={highlight} height={parameters.containerHeight.container - 2 + 'px'}>
            <Body height={bodyHeight + 'px'} fontSize={dataRow.fontSize + 'px'}>
                <input type="text"
                    name="name"
                    style={inputNameFieldStyle}
                    placeholder="Name..."
                    value={chatParams.name}
                    onChange={handleChange} />
                <br />
                <textarea type="text"
                    name="message"
                    style={inputMessageFieldStyle}
                    placeholder="Message..."
                    value={chatParams.message}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange} />
                <button
                    onClick={sendMessage}
                    disabled={chatParams.message.length === 0}
                    style={{ position: 'absolute', bottom: '12px', right: '10px', borderRadius: '50px', paddingLeft: '10px', paddingRight: '10px', paddingTop: '1px', paddingBottom: '1px', lineHeight: '1.2', fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'inherit', cursor: 'pointer' }}>
                    Send
                </button>
            </Body>
            <LoggingIndicator logStatus={loggingModule} />
        </Tile>
    );
}
